import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { QrCode, Copy, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UpiPaymentModal({ booking, onClose }) {
  const { authFetch } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/config`).then((r) => r.json()).then(setCfg).catch(() => {});
  }, []);

  const qrSrc = cfg?.upi?.qr_url?.startsWith("http") ? cfg.upi.qr_url : cfg?.upi?.qr_url;

  const upiIntent = () => {
    const vpa = cfg?.upi?.vpa;
    if (!vpa) return null;
    const params = new URLSearchParams({
      pa: vpa, pn: cfg.upi.name || "HeylpMate",
      am: String(booking.price_inr), cu: "INR",
      tn: `HeylpMate ${booking.service_name} #${booking.id.slice(0, 8)}`,
    });
    return `upi://pay?${params.toString()}`;
  };

  const copyBookingId = () => {
    navigator.clipboard.writeText(booking.id).then(() => toast.success("Booking ID copied"));
  };

  const submit = async () => {
    if (utr.trim().length < 6) { toast.error("Enter your UPI Reference / UTR number (min 6 chars)"); return; }
    setBusy(true);
    try {
      await authFetch({ method: "POST", url: `${API}/payments/upi/submit`, data: { booking_id: booking.id, utr: utr.trim() } });
      setDone(true);
      toast.success("Payment submitted — awaiting admin verification");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const intent = upiIntent();

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="upi-payment-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <QrCode className="w-5 h-5" style={{ color: "var(--secondary)" }} /> Pay via UPI
          </DialogTitle>
          <DialogDescription>
            Scan the HeylpMate UPI QR and pay <b>₹{booking.price_inr}</b> for your <b>{booking.service_name}</b> booking.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
            <div className="font-display text-2xl mt-3">Payment submitted</div>
            <p className="text-sm text-neutral-600 mt-2">
              We'll verify your UTR and confirm your booking within minutes.
              You can track this in your <b>dashboard</b>.
            </p>
            <button data-testid="upi-done-btn" onClick={onClose} className="btn-primary w-full mt-6">Go to dashboard</button>
          </div>
        ) : (
          <div className="mt-2 space-y-4">
            <div className="rounded-2xl border border-black/10 bg-white p-4 grid place-items-center">
              {qrSrc ? (
                <img
                  src={qrSrc}
                  alt="HeylpMate UPI QR code"
                  data-testid="upi-qr-image"
                  className="w-56 h-56 object-contain"
                />
              ) : (
                <div className="w-56 h-56 grid place-items-center text-neutral-400 text-sm">Loading QR…</div>
              )}
              {cfg?.upi?.vpa && (
                <div className="mt-3 text-xs text-neutral-500 text-center break-all">
                  UPI ID: <b>{cfg.upi.vpa}</b>
                </div>
              )}
              {intent && (
                <a
                  href={intent}
                  data-testid="upi-intent-link"
                  className="mt-3 text-xs font-bold underline"
                  style={{ color: "var(--secondary)" }}
                >
                  Open in UPI app →
                </a>
              )}
            </div>

            <div className="rounded-2xl bg-neutral-50 border border-black/5 p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Booking ID (add in UPI note)</div>
                <div className="font-mono text-sm">{booking.id.slice(0, 12)}…</div>
              </div>
              <button onClick={copyBookingId} data-testid="copy-booking-id" className="chip">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500">UPI Reference / UTR number</Label>
              <Input
                data-testid="upi-utr-input"
                className="mt-1.5"
                placeholder="e.g. 4283738492812"
                value={utr}
                onChange={(e) => setUtr(e.target.value)}
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                You'll find this in your UPI app's transaction history after paying.
              </p>
            </div>

            <button
              onClick={submit}
              disabled={busy}
              data-testid="upi-submit-btn"
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : "I've paid — submit UTR"}
            </button>
            <p className="text-[11px] text-neutral-500 text-center">
              Admin verifies within minutes. Booking confirms automatically on approval.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
